import React, { useState, useEffect } from "react";
import heart from "../../../assets/img/heart_j.svg";
import heartFilled from "../../../assets/img/heartFilled.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import MailOutline from "@mui/icons-material/MailOutline";
import TrendingUp from "@mui/icons-material/TrendingUp";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import Schedule from "@mui/icons-material/Schedule";
import EmojiEvents from "@mui/icons-material/EmojiEvents";
import Groups from "@mui/icons-material/Groups";
import { timeAgo, getCorsSafeMediaUrl } from "../../../Helper/CommonFunctions";
import { hitSetBookMarkJobByProfessionalApi } from "../../../Redux/Professional/UpdateJobFavSlice";
import views from "../../../assets/img/view jobs.svg";
import bidsIcon from "../../../assets/img/bids jobs 1.svg";
import creditsIcon from "../../../assets/img/credits jobs.svg";
import { showToast } from "../../../Helper/Toast";
import timeJobs from "../../../assets/img/time jobs.svg";
import locations from "../../../assets/img/location jobs.svg";
import BudgetRange from "../../../assets/img/Budget Range.svg";
import SquareFootage from "../../../assets/img/Square Footage.svg";
import LinearFeet from "../../../assets/img/Linear Feet.svg";
import Dimensions from "../../../assets/img/Dimensions.svg";
import media from "../../../assets/img/Media.svg";
import DimensionSummary from "../JobDetails/DimensionSummary/DimensionSummary";
import moment from "moment";
import { ApiBaseUrl } from "../../../Utils/Constants";

const PROFILE_PLACEHOLDER = "https://via.placeholder.com/50";
const JOB_MEDIA_PLACEHOLDER =
  "https://images.unsplash.com/photo-1560807707-8cc77767d783?w=400";

const getImageUrl = (imagePath) => {
  if (!imagePath || typeof imagePath !== "string") {
    return "";
  }

  if (/^(https?:)?\/\//i.test(imagePath) || imagePath.startsWith("data:")) {
    return imagePath;
  }

  const normalizedBaseUrl = ApiBaseUrl.endsWith("/")
    ? ApiBaseUrl.slice(0, -1)
    : ApiBaseUrl;
  const normalizedImagePath = imagePath.startsWith("/")
    ? imagePath
    : `/${imagePath}`;

  return `${normalizedBaseUrl}${normalizedImagePath}`;
};

const mediaSrc = (path) => getCorsSafeMediaUrl(getImageUrl(path)) || "";

const handleImageError = (event, fallbackSrc) => {
  if (event.currentTarget.src !== fallbackSrc) {
    event.currentTarget.src = fallbackSrc;
  }
};

const getJobDetailsCategoryLabel = (job) => {
  if (!job) return "General";
  const direct =
    job.serviceName ||
    job.tradeName ||
    job.jobCategoryName ||
    job.categoryName ||
    job.tenderTypeName;
  if (direct && String(direct).trim()) return String(direct).trim();
  if (Array.isArray(job.concerns) && job.concerns.length > 0) {
    const first = String(job.concerns[0] || "").trim();
    if (first) return first;
  }
  return "General";
};

const formatJobDetailsBudgetText = (job) => {
  if (job?.budgetMinimum === null) return "No Budget";
  if (job?.budgetMinimum === job?.budgetMaximum) {
    return `$${Number(job.budgetMaximum || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  const min = Number(job?.budgetMinimum || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const max = Number(job?.budgetMaximum || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${min} - $${max}`;
};

const getMeasurementCount = (jobData) => {
  const surfaceMeasurements = Array.isArray(jobData?.surfaceMeasurements)
    ? jobData.surfaceMeasurements.length
    : 0;

  if (surfaceMeasurements > 0) {
    return surfaceMeasurements;
  }

  return Array.isArray(jobData?.measurements) ? jobData.measurements.length : 0;
};

const buildClientAddressLine = (job) => {
  if (!job) return "";
  const u = job.userId || {};
  const street =
    job.streetAddress ||
    job.address ||
    job.addressLine1 ||
    job.fullAddress ||
    u.address ||
    u.streetAddress ||
    u.addressLine1 ||
    "";
  const city = job.city || u.city || "";
  const state = job.state || u.state || "";
  const zip = job.zipCode || job.zip || u.zipCode || u.zip || "";
  const country = job.country || u.country || "USA";
  const cityState = [city, state].filter(Boolean).join(", ");
  const parts = [];
  if (street) parts.push(street);
  if (cityState) parts.push(cityState);
  if (zip) parts.push(zip);
  if (country) parts.push(country);
  if (parts.length > 0) return parts.join(", ");
  return "";
};

const formatClientAwardRateStat = (user) => {
  const v = user?.awardRate ?? user?.clientAwardRate ?? user?.awardRatePercent;
  if (v === null || v === undefined || v === "") return "N/A";
  if (typeof v === "number" && Number.isFinite(v)) return String(Math.round(v));
  const s = String(v).trim();
  return s === "" ? "N/A" : s;
};

const parseClientAwardRatePercent = (user) => {
  const v = user?.awardRate ?? user?.clientAwardRate ?? user?.awardRatePercent;
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v))
    return Math.min(100, Math.max(0, v));
  const s = String(v).replace(/%/g, "").trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
};

const clientScore = (user, ...keys) => {
  for (const k of keys) {
    const v = user?.[k];
    if (v !== null && v !== undefined && v !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) return Math.min(100, Math.max(0, n));
    }
  }
  return 0;
};

function JobDetailsClientSemiGauge({ displayValue, fillPercent, fill }) {
  const pct = Math.min(100, Math.max(0, Number(fillPercent) || 0));
  const stroke = 5;
  const r = 22;
  const cx = 30;
  const cy = 27;
  const arcLen = Math.PI * r;
  const dash = arcLen * (pct / 100);

  return (
    <div className="job-details-client-gauge">
      <svg
        className="job-details-client-gauge__svg"
        viewBox="0 0 60 34"
        aria-hidden
      >
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        {pct > 0 && (
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
            fill="none"
            stroke={fill}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${arcLen}`}
          />
        )}
      </svg>
      <span className="job-details-client-gauge__value">{displayValue}</span>
    </div>
  );
}

const JobDetails = ({
  jobData,
  jobListData,
  setJobListData,
  openModal,
  onBackToJobs,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [localJobData, setLocalJobData] = useState(jobData);

  const [activeTab, setActiveTab] = useState("details");
  const [showImg, setShowImg] = useState(false);
  const [showImg1, setShowImg1] = useState(false);

  const handleSubmitBid = (job) => {
    navigate(`/professional/job-details/${job._id}?focus=bid`);
  };

  const handleSendMessage = () => {
    navigate("/professional/messages");
  };

  useEffect(() => {
    setLocalJobData(jobData);
  }, [jobData]);

  const updateBookmark = (isBookmark) => {
    dispatch(
      hitSetBookMarkJobByProfessionalApi({
        jobId: jobData?._id,
        type: isBookmark === 1 ? 0 : 1,
      })
    ).then((res) => {
      if (res.payload && res.payload.status === true) {
        if (isBookmark === 1) {
          showToast("Job removed from bookmarks", "success");
        } else {
          showToast("Job bookmarked successfully", "success");
        }
        const updatedJobData = {
          ...localJobData,
          isBookmark: isBookmark === 1 ? 0 : 1,
        };
        setLocalJobData(updatedJobData);

        if (typeof setJobListData === "function" && Array.isArray(jobListData)) {
          const updatedJobList = jobListData.map((job) => {
            if (job._id === jobData?._id) {
              return { ...job, isBookmark: isBookmark === 1 ? 0 : 1 };
            }
            return job;
          });
          setJobListData(updatedJobList);
        }
      }
    });
  };

  const measurementCount = getMeasurementCount(localJobData);
  const bidCount = Number(localJobData?.bidCount) || 0;
  const viewCount = Number(localJobData?.viewCount) || 0;
  const creditsCost = Number(localJobData?.isReadyToGo) === 1 ? 10 : 5;

  const clientUser = localJobData?.userId || {};
  const clientClarity = clientScore(
    clientUser,
    "projectClarityScore",
    "projectClarity",
    "clarityScore"
  );
  const clientEngagement = clientScore(
    clientUser,
    "engagementScore",
    "engagement",
    "clientEngagementScore"
  );
  const clientTimelyPayment = clientScore(
    clientUser,
    "timelyPaymentScore",
    "timelyPayment",
    "paymentTimelinessScore"
  );
  const clientAwardRateStat = formatClientAwardRateStat(clientUser);
  const clientAwardPct = parseClientAwardRatePercent(clientUser);
  const clientAwardGaugeDisplay =
    clientAwardRateStat === "N/A"
      ? "N/A"
      : clientAwardPct !== null
        ? String(Math.round(clientAwardPct))
        : "0";
  const clientAwardGaugeFill =
    clientAwardRateStat === "N/A" ? 0 : clientAwardPct ?? 0;

  return (
    <div className="job-details-page">
      <header className="job-details-page__toolbar">
        {typeof onBackToJobs === "function" && (
          <button
            type="button"
            className="job-details-page__back"
            aria-label="Back to jobs"
            onClick={onBackToJobs}
          >
            <ChevronLeft sx={{ fontSize: 28 }} />
          </button>
        )}
        <h1 className="job-details-page__title">Jobs Details</h1>
      </header>

      <section className="job-details-summary-card">
        <div className="job-details-summary-card__category">
          <LocalOfferOutlined
            className="job-details-summary-card__category-icon"
            sx={{ fontSize: 18 }}
          />
          <span>{getJobDetailsCategoryLabel(localJobData)}</span>
        </div>

        <div className="job-details-summary-card__title-row">
          <h2 className="job-details-summary-card__title">
            {localJobData?.title}
          </h2>
          <button
            type="button"
            className="job-details-summary-card__bookmark"
            aria-label={
              Number(localJobData?.isBookmark) === 1
                ? "Remove bookmark"
                : "Bookmark job"
            }
            onClick={() => updateBookmark(localJobData?.isBookmark)}
          >
            <img
              src={
                Number(localJobData?.isBookmark) === 1 ? heartFilled : heart
              }
              alt=""
            />
          </button>
        </div>

        <div className="job-details-summary-card__meta-row">
          <span className="job-details-summary-card__meta-loc">
            <img src={locations} alt="" />
            {localJobData?.city}, {localJobData?.state}
            <span className="job-details-summary-card__dot">·</span>
            {localJobData?.isPlacedInYourServiceArea === 1
              ? "In Area"
              : "Out of Area"}
          </span>
          <span className="job-details-summary-card__meta-time">
            <img src={timeJobs} alt="" />
            {timeAgo(localJobData?.createdAt)}
          </span>
        </div>

        <div className="job-details-summary-card__price-row">
          <div
            className={`job-details-summary-card__price${
              localJobData?.budgetMinimum === null ? " is-muted" : ""
            }`}
          >
            {formatJobDetailsBudgetText(localJobData)}
          </div>
          <div className="job-details-summary-card__expiring">
            Expiring:{" "}
            {localJobData?.installedDateEnd &&
            moment(localJobData.installedDateEnd).isValid()
              ? moment(localJobData.installedDateEnd).format("MM/DD/YYYY")
              : "—"}
          </div>
        </div>

        <div className="job-details-summary-card__stats-row">
          <div className="job-details-summary-card__stats-inline">
            <span>
              <img src={bidsIcon} alt="" />
              {bidCount} {bidCount === 1 ? "bid" : "bids"}
            </span>
            <span>
              <img src={views} alt="" />
              {viewCount} {viewCount === 1 ? "view" : "views"}
            </span>
          </div>
          <span className="job-details-summary-card__credits-pill">
            <img src={creditsIcon} alt="" />
            {creditsCost} credits
          </span>
        </div>

        <div className="job-details-summary-card__actions">
          <button type="button" className="job-details-btn job-details-btn--info" onClick={openModal}>
            Request More Information
          </button>
          <button
            type="button"
            className="job-details-btn job-details-btn--primary"
            onClick={() => handleSubmitBid(localJobData)}
          >
            {Number(localJobData?.isBidPlaced) === 1
              ? "View My Bid"
              : "Submit Your Bid"}
          </button>
        </div>
      </section>

      <section className="project-tabs job-details-body-tabs-root">
        <nav className="tabs job-details-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "details"}
            className={activeTab === "details" ? "active" : ""}
            onClick={() => setActiveTab("details")}
          >
            Project Details
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "Client"}
            className={activeTab === "Client" ? "active" : ""}
            onClick={() => setActiveTab("Client")}
          >
            Client
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "bids"}
            className={activeTab === "bids" ? "active" : ""}
            onClick={() => setActiveTab("bids")}
          >
            Bids ({localJobData?.bidCount || 0})
          </button>
        </nav>

        <div className="tab-content">
          {activeTab === "details" && (
            <>
              <section className="description job-details-desc">
                <h5>Project Description</h5>
                <p>{localJobData?.description}</p>
              </section>

              <section className="details job-details-spec-list">
                <h5 className="job-details-spec-list__title">
                  <span
                    className="job-details-spec-list__bullet"
                    aria-hidden
                  />
                  Project Details
                </h5>

                <div className="job-details-detail-row job-details-detail-row--static">
                  <div className="job-details-detail-row__left">
                    <span className="job-details-detail-row__icon-wrap">
                      <img src={SquareFootage} alt="" />
                    </span>
                    <div>
                      <p className="job-details-detail-row__title">
                        Square Footage
                      </p>
                      <span className="job-details-detail-row__sub">
                        Total Area
                      </span>
                    </div>
                  </div>
                  <div className="job-details-detail-row__right">
                    <span className="job-details-detail-row__value">
                      {localJobData?.totalSquareFoot ?? 0} ft²
                    </span>
                  </div>
                </div>

                <div className="job-details-detail-row job-details-detail-row--static">
                  <div className="job-details-detail-row__left">
                    <span className="job-details-detail-row__icon-wrap">
                      <img src={LinearFeet} alt="" />
                    </span>
                    <div>
                      <p className="job-details-detail-row__title">Perimeter</p>
                      <span className="job-details-detail-row__sub">
                        Total length
                      </span>
                    </div>
                  </div>
                  <div className="job-details-detail-row__right">
                    <span className="job-details-detail-row__value">
                      {localJobData?.linearSquareFoot ?? 0} ft
                    </span>
                  </div>
                </div>

                <div className="job-details-detail-row">
                  <div className="job-details-detail-row__left">
                    <span className="job-details-detail-row__icon-wrap">
                      <img src={Dimensions} alt="" />
                    </span>
                    <div>
                      <p className="job-details-detail-row__title">Dimensions</p>
                      <span className="job-details-detail-row__sub">
                        Layout info
                      </span>
                    </div>
                  </div>
                  <div className="job-details-detail-row__right">
                    <span className="job-details-detail-row__value">
                      {measurementCount} Areas
                    </span>
                    <button
                      type="button"
                      className="job-details-detail-row__link"
                      onClick={() => setShowImg1((v) => !v)}
                    >
                      View Details
                    </button>
                    <button
                      type="button"
                      className="job-details-detail-row__chev-btn"
                      aria-label={
                        showImg1 ? "Hide dimensions details" : "View dimensions"
                      }
                      onClick={() => setShowImg1((v) => !v)}
                    >
                      <ChevronRight sx={{ fontSize: 22 }} />
                    </button>
                  </div>
                </div>
                {showImg1 && (
                  <DimensionSummary
                    jobId={localJobData?._id}
                    initialJobData={localJobData}
                  />
                )}

                <div className="job-details-detail-row">
                  <div className="job-details-detail-row__left">
                    <span className="job-details-detail-row__icon-wrap">
                      <img src={media} alt="" />
                    </span>
                    <div>
                      <p className="job-details-detail-row__title">Media</p>
                      <span className="job-details-detail-row__sub">
                        Photos &amp; videos
                      </span>
                    </div>
                  </div>
                  <div className="job-details-detail-row__right">
                    <span className="job-details-detail-row__value">
                      {localJobData?.media?.length || 0} Files
                    </span>
                    <button
                      type="button"
                      className="job-details-detail-row__link"
                      onClick={() => setShowImg((v) => !v)}
                    >
                      View Gallery
                    </button>
                    <button
                      type="button"
                      className="job-details-detail-row__chev-btn"
                      aria-label={showImg ? "Hide gallery" : "View gallery"}
                      onClick={() => setShowImg((v) => !v)}
                    >
                      <ChevronRight sx={{ fontSize: 22 }} />
                    </button>
                  </div>
                </div>
                {showImg &&
                  (localJobData?.media && localJobData.media.length > 0 ? (
                    <div className="job-details-gallery-expand">
                      {localJobData.media.map((mediaUrl, index) => (
                        <div
                          key={index}
                          className="job-details-photos__wrap job-details-gallery-expand__item"
                        >
                          {index === 0 && (
                            <span className="job-details-photos__badge">Main</span>
                          )}
                          <img
                            src={mediaSrc(mediaUrl)}
                            alt=""
                            onError={(event) =>
                              handleImageError(event, JOB_MEDIA_PLACEHOLDER)
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="job-details-gallery-expand">
                      <div className="job-details-photos__wrap job-details-gallery-expand__item">
                        <span className="job-details-photos__badge">Main</span>
                        <img src={JOB_MEDIA_PLACEHOLDER} alt="" />
                      </div>
                    </div>
                  ))}
              </section>

              <section className="budget-box job-details-budget-card">
                <div className="budget">
                  <div className="budget-info">
                    <img src={BudgetRange} alt="" />
                    <div style={{ whiteSpace: "nowrap" }}>
                      <h4>Budget Range</h4>
                      <span>Total Project cost</span>
                    </div>
                  </div>

                  <div className="job-details-budget-card__value-wrap">
                    <h4 className="job-details-budget-card__amount">
                      {formatJobDetailsBudgetText(localJobData)}
                    </h4>
                  </div>
                </div>
              </section>

              <section className="concerns job-details-concerns">
                <h5>Concerns</h5>
                <div className="tags">
                  {localJobData?.concerns &&
                    localJobData.concerns.map((concern, index) => (
                      <span key={index}>{concern.trim()}</span>
                    ))}
                </div>
              </section>

              <p className="job-details-measurement-disclaimer">
                * Client Provided measurements are estimates and may vary.
              </p>
            </>
          )}

          {activeTab === "Client" && (
            <>
              <div className="job-details-client-card">
                <div className="job-details-client-card__profile">
                  <img
                    src={
                      mediaSrc(localJobData?.userId?.profileImage) ||
                      PROFILE_PLACEHOLDER
                    }
                    alt=""
                    className="job-details-client-card__avatar"
                    onError={(event) =>
                      handleImageError(event, JOB_MEDIA_PLACEHOLDER)
                    }
                  />
                  <div className="job-details-client-card__identity">
                    <h3 className="job-details-client-card__name">
                      {[localJobData?.userId?.firstName, localJobData?.userId?.lastName]
                        .filter(Boolean)
                        .join(" ") || "Client"}
                    </h3>
                    <p className="job-details-client-card__address">
                      {buildClientAddressLine(localJobData) || "—"}
                    </p>
                  </div>
                </div>
                <div className="job-details-client-card__stats">
                  <div className="job-details-client-stat">
                    <div className="job-details-client-stat__num">
                      {localJobData?.userId?.totalCompletedJobs ?? 0}
                    </div>
                    <div className="job-details-client-stat__label">
                      Projects Completed
                    </div>
                  </div>
                  <div className="job-details-client-stat">
                    <div className="job-details-client-stat__num job-details-client-stat__num--text">
                      {formatClientAwardRateStat(clientUser)}
                    </div>
                    <div className="job-details-client-stat__label">Award Rate</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="job-details-client-card__message-btn"
                  onClick={handleSendMessage}
                >
                  <MailOutline sx={{ fontSize: 22 }} />
                  Send Message
                </button>
              </div>

                <div className="job-details-client-ratings">
                <div className="job-details-client-ratings__header">
                  <h4 className="job-details-client-ratings__title">
                    Client Ratings
                  </h4>
                  <button
                    type="button"
                    className="job-details-client-ratings__view-all"
                  >
                    View All
                  </button>
                </div>
                <div className="job-details-client-rating-row">
                  <span className="job-details-client-rating-row__icon job-details-client-rating-row__icon--clarity">
                    <TrendingUp sx={{ fontSize: 18 }} />
                  </span>
                  <span className="job-details-client-rating-row__label">
                    Project Clarity
                  </span>
                  <JobDetailsClientSemiGauge
                    displayValue={String(Math.round(clientClarity))}
                    fillPercent={clientClarity}
                    fill="#2563eb"
                  />
                </div>
                <div className="job-details-client-rating-row">
                  <span className="job-details-client-rating-row__icon job-details-client-rating-row__icon--engage">
                    <ChatBubbleOutline sx={{ fontSize: 18 }} />
                  </span>
                  <span className="job-details-client-rating-row__label">
                    Engagement
                  </span>
                  <JobDetailsClientSemiGauge
                    displayValue={String(Math.round(clientEngagement))}
                    fillPercent={clientEngagement}
                    fill="#16a34a"
                  />
                </div>
                <div className="job-details-client-rating-row">
                  <span className="job-details-client-rating-row__icon job-details-client-rating-row__icon--time">
                    <Schedule sx={{ fontSize: 18 }} />
                  </span>
                  <span className="job-details-client-rating-row__label">
                    Timely Payment
                  </span>
                  <JobDetailsClientSemiGauge
                    displayValue={String(Math.round(clientTimelyPayment))}
                    fillPercent={clientTimelyPayment}
                    fill="#ea580c"
                  />
                </div>
                <div className="job-details-client-rating-row">
                  <span className="job-details-client-rating-row__icon job-details-client-rating-row__icon--award">
                    <EmojiEvents sx={{ fontSize: 18 }} />
                  </span>
                  <span className="job-details-client-rating-row__label">
                    Award Rate
                  </span>
                  <JobDetailsClientSemiGauge
                    displayValue={clientAwardGaugeDisplay}
                    fillPercent={clientAwardGaugeFill}
                    fill="#9333ea"
                  />
                </div>
              </div>

              <p className="job-details-measurement-disclaimer job-details-client-disclaimer">
                * Client Provided measurements are estimates and may vary.
              </p>
            </>
          )}

          {activeTab === "bids" && (
            <>
              <div className="job-details-bids-panel">
                <h3 className="job-details-bids-panel__title">Bids</h3>
                <div className="job-details-bids-card">
                  <div className="job-details-bids-card__icon-wrap" aria-hidden>
                    <Groups sx={{ fontSize: 52, color: "#9ca3af" }} />
                  </div>
                  {Number(localJobData?.isBidPlaced) === 1 ? (
                    <>
                      <p className="job-details-bids-card__primary">
                        1 Bid Submitted
                      </p>
                      <p className="job-details-bids-card__secondary">
                        Bid details are private and only visible to the client
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="job-details-bids-card__primary">
                        No bid submitted yet
                      </p>
                      <p className="job-details-bids-card__secondary">
                        Submit your bid to participate in this project. Other
                        professionals&apos; bids are private.
                      </p>
                    </>
                  )}
                </div>
              </div>
              <p className="job-details-measurement-disclaimer job-details-bids-disclaimer">
                * Client Provided measurements are estimates and may vary.
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default JobDetails;
